import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import supabase from '../../supabase';
import './QuoteAnalyticGraph.css';

const QuoteAnalyticGraph = ({ user, onSectorClick, selectedPeriod }) => {
  const [quoteData, setQuoteData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [stats, setStats] = useState({
    totalQuotes: 0,
    draftQuotes: 0,
    acceptedQuotes: 0,
    notAcceptedQuotes: 0,
  });

  // Color mapping by status name
  const COLOR_MAP = {
    'Draft': '#6B7280',
    'Accepted': '#10B981',
    'Not Accepted': '#EF4444'
  };

  // Helper function to get date range based on selected period
  const getDateRange = (period) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    switch (period) {
      case 'this-month': {
        const start = new Date(currentYear, currentMonth, 1);
        const end = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);
        return { start, end };
      }
      case 'last-month': {
        const start = new Date(currentYear, currentMonth - 1, 1);
        const end = new Date(currentYear, currentMonth, 0, 23, 59, 59);
        return { start, end };
      }
      case 'last-3-months': {
        const start = new Date(currentYear, currentMonth - 2, 1);
        const end = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);
        return { start, end };
      }
      case 'last-6-months': {
        const start = new Date(currentYear, currentMonth - 5, 1);
        const end = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);
        return { start, end };
      }
      case 'this-year': {
        const start = new Date(currentYear, 0, 1);
        const end = new Date(currentYear, 11, 31, 23, 59, 59);
        return { start, end };
      }
      case 'last-year': {
        const start = new Date(currentYear - 1, 0, 1);
        const end = new Date(currentYear - 1, 11, 31, 23, 59, 59);
        return { start, end };
      }
      case 'all-time':
      default:
        return { start: null, end: null }; // No date filtering
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchQuoteAnalyticData();
    }
  }, [user?.id, selectedPeriod]); // Add selectedPeriod as dependency

  const fetchQuoteAnalyticData = async () => {
    try {
      setLoading(true);
      setHasError(false);

      // Build the query based on selected period
      let query = supabase
        .from('quotes')
        .select('id, email_sent, status, created_at')
        .eq('user_id', user.id);

      // Apply date filtering if not "all-time"
      const { start, end } = getDateRange(selectedPeriod);
      if (start && end) {
        query = query
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString());
      }

      const { data: allQuotes, error } = await query;

      if (error) throw error;

      let draft = 0;
      let accepted = 0;
      let notAccepted = 0;

      allQuotes.forEach(quote => {
        if (!quote.email_sent) {
          // Draft: email_sent == false
          draft += 1;
        } else if (quote.email_sent && quote.status === true) {
          // Accepted: email_sent == true & status == true
          accepted += 1;
        } else if (quote.email_sent && quote.status === false) {
          // Not Accepted: email_sent == true & status == false
          notAccepted += 1;
        }
      });

      setQuoteData(
        [
          { name: 'Draft', value: draft },
          { name: 'Accepted', value: accepted },
          { name: 'Not Accepted', value: notAccepted },
        ].filter(item => item.value > 0)
      );

      setStats({
        totalQuotes: allQuotes.length,
        draftQuotes: draft,
        acceptedQuotes: accepted,
        notAcceptedQuotes: notAccepted,
      });

    } catch (error) {
      console.error('Error fetching quote analytics:', error);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const totalQuotes = stats.totalQuotes;

  const handleSectorClick = (data, index) => {
    const selectedStatus = data.name; // Draft, Accepted, Not Accepted
    // Pass both the tab name and the filter status
    onSectorClick && onSectorClick('QuotationTracker', selectedStatus);
  };

  const handleCreateQuote = () => {
    // Use the onSectorClick callback to switch to the Quotations tab (same pattern as InvoiceStatusTracker)
    onSectorClick && onSectorClick('Quotations');
  };

  // Get period display text
  const getPeriodDisplayText = (period) => {
    switch (period) {
      case 'this-month': return 'This Month';
      case 'last-month': return 'Last Month';
      case 'last-3-months': return 'Last 3 Months';
      case 'last-6-months': return 'Last 6 Months';
      case 'this-year': return 'This Year';
      case 'last-year': return 'Last Year';
      case 'all-time': return 'All Time';
      default: return 'All Time';
    }
  };

  if (loading) {
    return (
      <div className="quoteanalyticgraph">
        <div className="quoteanalyticgraph-loading">
          <div className="quoteanalyticgraph-loading-spinner"></div>
          <p>Loading quote analytics...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="quoteanalyticgraph">
        <div className="quoteanalyticgraph-error">
          <p>Failed to load quote analytics.</p>
          <button onClick={fetchQuoteAnalyticData} className="quoteanalyticgraph-retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quoteanalyticgraph">
      <div className="quoteanalyticgraph-section">
        <div className="quoteanalyticgraph-section-header">
          <h2 className="quoteanalyticgraph-section-title">
            Quote Analytics - {getPeriodDisplayText(selectedPeriod)}
          </h2>
          <button 
            className="quoteanalyticgraph-create-btn"
            onClick={handleCreateQuote}
          >
            Create Quote
          </button>
        </div>

        <div className="quoteanalyticgraph-content">
          <div className="quoteanalyticgraph-summary">
            <div 
              className="quoteanalyticgraph-summary-card quoteanalyticgraph-draft"
              onClick={() => handleSectorClick({ name: 'Draft' }, 0)}
              style={{ cursor: 'pointer' }}
            >
              <p className="quoteanalyticgraph-summary-label">Draft Quotes</p>
              <h3 className="quoteanalyticgraph-summary-count">{stats.draftQuotes}</h3>
            </div>
            <div 
              className="quoteanalyticgraph-summary-card quoteanalyticgraph-accepted"
              onClick={() => handleSectorClick({ name: 'Accepted' }, 1)}
              style={{ cursor: 'pointer' }}
            >
              <p className="quoteanalyticgraph-summary-label">Accepted Quotes</p>
              <h3 className="quoteanalyticgraph-summary-count">{stats.acceptedQuotes}</h3>
            </div>
            <div 
              className="quoteanalyticgraph-summary-card quoteanalyticgraph-notaccepted"
              onClick={() => handleSectorClick({ name: 'Not Accepted' }, 2)}
              style={{ cursor: 'pointer' }}
            >
              <p className="quoteanalyticgraph-summary-label">Not Accepted</p>
              <h3 className="quoteanalyticgraph-summary-count">{stats.notAcceptedQuotes}</h3>
            </div>
          </div>

          <div className="quoteanalyticgraph-chart-container">
            {totalQuotes > 0 ? (
              <div className="quoteanalyticgraph-chart-wrapper">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={quoteData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={0}
                    >
                      {quoteData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLOR_MAP[entry.name]}
                          onClick={() => handleSectorClick(entry, index)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} Quotes`, name]} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="quoteanalyticgraph-no-data">
                <p>No quotes found for {getPeriodDisplayText(selectedPeriod).toLowerCase()}.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteAnalyticGraph;